import { makeObservable, observable, action } from 'mobx';
import { FactorEnrollmentStatus } from '@wix/ambassador-identity-factors-v1-identity-factor/types';
import { WebAuthnApi } from '../services/webAuthnApi';
import {
  EnrollmentStatus,
  PublicKeyCredentialWithJSON,
} from '../types/webAuthnEnrollmentTypes';
import { CreatePasskeyErrorCode } from '../types/createPasskeyMessages';
import { isWebAuthnSupported } from '../utils/webauthnCompat';
import { NotificationStore } from '../utils/notificationStore';
import type { AppStore } from './AppStore';
import { HttpError } from '@wix/http-client';

const INVALID_PASSWORD_ERROR_CODE = 9960;

export class WebAuthnEnrollmentStore {
  private readonly appStore?: AppStore;
  private readonly webAuthnApi: WebAuthnApi;

  enrollmentStatus: EnrollmentStatus = 'idle';
  passwordError: string | null = null;
  passwordStepNotification: NotificationStore = new NotificationStore();

  constructor(appStore?: AppStore) {
    this.appStore = appStore;
    this.webAuthnApi = new WebAuthnApi();

    makeObservable(this, {
      enrollmentStatus: observable,
      passwordError: observable,
      passwordStepNotification: observable,
      verifyPassword: action.bound,
      enrollWebAuthn: action.bound,
      sendForgotPasswordEmail: action.bound,
      clearPasswordError: action.bound,
      reset: action.bound,
    });
  }

  async verifyPassword(password: string): Promise<boolean> {
    if (!this.appStore) {
      throw new Error('verifyPassword requires AppStore');
    }
    this.enrollmentStatus = 'authenticating';
    this.passwordError = null;

    try {
      await this.appStore.accountSettingsApi.authenticate(password);
      return true;
    } catch (error) {
      this.enrollmentStatus = 'error';
      const errorCode = (error as HttpError).response?.data?.errorCode;
      if (errorCode === INVALID_PASSWORD_ERROR_CODE) {
        this.passwordStepNotification.close();
        this.passwordError =
          'passkey.popups.creation.confirmModal.error.wrongPassword';
      } else {
        this.passwordStepNotification.open({
          content: 'passkey.popups.creation.confirmModal.error.general',
          theme: 'error',
        });
      }
      return false;
    }
  }

  async enrollWebAuthn(): Promise<
    | { success: true; isBackedUp: boolean }
    | {
        success: false;
        errorCode: CreatePasskeyErrorCode;
        errorMessage: string;
      }
  > {
    this.enrollmentStatus = 'enrolling';

    if (!isWebAuthnSupported()) {
      this.enrollmentStatus = 'error';
      return {
        success: false,
        errorCode: CreatePasskeyErrorCode.NOT_SUPPORTED,
        errorMessage: 'WebAuthn is not supported in this browser',
      };
    }

    try {
      const challengeResponse = await this.webAuthnApi.enrollWebAuthn();

      if (
        challengeResponse?.factorEnrollStatus !==
        FactorEnrollmentStatus.ENROLLMENT_REQUIRES_ADDITIONAL_STEP
      ) {
        throw new Error('Unexpected enrollment status');
      }

      const credentialCreationData =
        challengeResponse.enrollmentRequiresAdditionalStepData?.webauthnData;

      if (!credentialCreationData?.publicKeyCredentialCreationOptionsJson) {
        throw new Error('Missing WebAuthn creation options');
      }

      const { publicKey } = JSON.parse(
        credentialCreationData.publicKeyCredentialCreationOptionsJson,
      );
      const parsedCreationOptions = (
        PublicKeyCredential as unknown as PublicKeyCredentialWithJSON
      ).parseCreationOptionsFromJSON(publicKey);

      const newCredential = (await navigator.credentials.create({
        publicKey: parsedCreationOptions,
      })) as PublicKeyCredential | null;

      if (!newCredential) {
        throw new Error('Credential creation cancelled');
      }

      const completionResponse = await this.webAuthnApi.enrollWebAuthn(
        newCredential,
      );

      if (
        completionResponse?.factorEnrollStatus !==
        FactorEnrollmentStatus.ENROLLMENT_COMPLETE
      ) {
        throw new Error('Enrollment failed');
      }

      const isBackedUp = this.extractBackupState(newCredential);

      this.enrollmentStatus = 'success';
      return { success: true, isBackedUp };
    } catch (error) {
      this.enrollmentStatus = 'error';
      const { errorCode, errorMessage } = this.parseEnrollmentError(error);
      return {
        success: false,
        errorCode,
        errorMessage,
      };
    }
  }

  private extractBackupState(credential: PublicKeyCredential): boolean {
    try {
      const response = credential.response as AuthenticatorAttestationResponse;
      const authenticatorData = response.getAuthenticatorData();
      const flags = new Uint8Array(authenticatorData)[32];
      // Bit 4 (0x10) is the BS (Backup State) flag
      return (flags & 0x10) !== 0;
    } catch {
      return false;
    }
  }

  private parseEnrollmentError(error: unknown): {
    errorCode: CreatePasskeyErrorCode;
    errorMessage: string;
  } {
    if (error instanceof DOMException) {
      const errorMessage = `${error.name}: ${error.message}`;

      if (error.name === 'InvalidStateError') {
        return {
          errorCode: CreatePasskeyErrorCode.DUPLICATE_PASSKEY,
          errorMessage,
        };
      }

      if (error.name === 'NotAllowedError') {
        return {
          errorCode: CreatePasskeyErrorCode.CANCELLED,
          errorMessage,
        };
      }

      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        return {
          errorCode: CreatePasskeyErrorCode.TIMEOUT,
          errorMessage,
        };
      }

      return {
        errorCode: CreatePasskeyErrorCode.GENERAL,
        errorMessage,
      };
    }

    if (error instanceof Error) {
      return {
        errorCode: CreatePasskeyErrorCode.GENERAL,
        errorMessage: `Error: ${error.message}`,
      };
    }

    return {
      errorCode: CreatePasskeyErrorCode.GENERAL,
      errorMessage: `Unknown: ${String(error)}`,
    };
  }

  async sendForgotPasswordEmail(): Promise<void> {
    if (!this.appStore) {
      throw new Error('sendForgotPasswordEmail requires AppStore');
    }
    const email = this.appStore.userDetails?.email;
    if (!email) {
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.error.general',
        theme: 'error',
      });
      return;
    }

    try {
      await this.appStore.accountSettingsApi.sendResetPasswordEmail(email);
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.success.notification',
        theme: 'success',
      });
    } catch {
      this.passwordStepNotification.open({
        content: 'passkey.popups.creation.confirmModal.error.general',
        theme: 'error',
      });
    }
  }

  clearPasswordError(): void {
    this.passwordError = null;
  }

  reset(): void {
    this.enrollmentStatus = 'idle';
    this.passwordError = null;
    this.passwordStepNotification.close();
  }
}
